<template>
  <div class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h3><i class="fa fa-clipboard"></i> Send Form</h3>
        <button class="close-btn" @click="$emit('cancel')">
          <i class="fa fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <div class="search-section">
          <div class="search-input-container">
            <i class="fa fa-search"></i>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search forms..."
              class="search-input"
              @input="filterForms"
              ref="searchInput"
            />
          </div>
        </div>
        
        <div class="form-list" v-if="filteredForms.length > 0">
          <div 
            v-for="form in filteredForms" 
            :key="form.FormIDForWeb"
            class="form-item"
            :class="{ 'selected': selectedForm && selectedForm.FormIDForWeb === form.FormIDForWeb }"
            @click="selectForm(form)"
          >
            <div class="form-info">
              <div class="form-title">{{ form.Name || form.FormName }}</div>
              <div class="form-description" v-if="form.Description">
                {{ form.Description }}
              </div>
              <div class="form-meta">
                <span class="form-type" :class="`type-${form.FormType?.toLowerCase()}`">
                  {{ form.FormType || 'Form' }}
                </span>
                <span class="form-updated" v-if="form.DateUpdated">
                  Updated {{ formatDate(form.DateUpdated) }}
                </span>
              </div>
            </div>
            <div class="form-actions">
              <i class="fa fa-external-link" title="Preview" @click.stop="previewForm(form)"></i>
            </div>
          </div>
        </div>
        
        <div v-else-if="loading" class="loading-state">
          <i class="fa fa-spinner fa-spin"></i>
          <span>Loading forms...</span>
        </div>
        
        <div v-else class="empty-state">
          <i class="fa fa-search"></i>
          <span>No forms found</span>
        </div>
        
        <!-- Selected Form Preview -->
        <div v-if="selectedForm" class="selected-preview">
          <h4>Selected Form:</h4>
          <div class="preview-card">
            <strong>{{ selectedForm.Name || selectedForm.FormName }}</strong>
            <p v-if="selectedForm.Description">{{ selectedForm.Description }}</p>
            <div class="preview-details">
              <div class="detail-item">
                <span class="label">Type:</span>
                <span class="value">{{ selectedForm.FormType || 'Form' }}</span>
              </div>
              <div class="detail-item">
                <span class="label">URL:</span>
                <span class="value">{{ getFormUrl(selectedForm) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button 
          class="btn btn-primary"
          @click="sendForm"
          :disabled="!selectedForm"
        >
          <i class="fa fa-paper-plane"></i>
          Send Form Link
        </button>
        <button class="btn btn-secondary" @click="$emit('cancel')">
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import mixin from '@/mixin.js'

export default {
  name: 'FormSelectionModal',
  mixins: [mixin],
  data() {
    return {
      searchQuery: '',
      selectedForm: null,
      filteredForms: [],
      isLoading: true,
      forms: [] // Store forms locally like PatientSidebar
    }
  },
  computed: {
    availableForms() {
      return this.forms || []
    }
  },
  async mounted() {
    // Focus search input
    this.$nextTick(() => {
      this.$refs.searchInput?.focus()
    })
    
    // Load forms if not already loaded
    await this.loadForms()
    this.filterForms()
  },
  methods: {
    async loadForms() {
      try {
        this.isLoading = true
        
        // Check if forms are already loaded
        if (!this.availableForms.length) {
          // Load forms from the store action (same as PatientSidebar)
          const result = await this.$store.dispatch('forms/getForms', {
            siteID: this.siteID
          })
          console.log('[FormModal] Raw result from getForms:', result)
          // Store forms locally - handle both direct array and object with forms property
          if (result) {
            if (Array.isArray(result)) {
              this.forms = result
              console.log('[FormModal] Loaded forms (array):', this.forms)
            } else if (result.forms) {
              this.forms = result.forms
              console.log('[FormModal] Loaded forms (object.forms):', this.forms)
            }
          }
        }
        
        this.isLoading = false
      } catch (error) {
        console.error('Failed to load forms:', error)
        this.isLoading = false
      }
    },
    
    filterForms() {
      if (!this.searchQuery.trim()) {
        this.filteredForms = this.availableForms.slice(0, 20) // Limit to first 20
        return
      }
      
      const query = this.searchQuery.toLowerCase()
      this.filteredForms = this.availableForms.filter(form => 
        form.Name?.toLowerCase().includes(query) ||
        form.FormName?.toLowerCase().includes(query) ||
        form.Description?.toLowerCase().includes(query) ||
        form.FormType?.toLowerCase().includes(query)
      ).slice(0, 20)
    },
    
    selectForm(form) {
      this.selectedForm = form
    },
    
    async previewForm(form) {
      console.log('[FormModal] Form object for preview:', form)
      
      // Check if form has a ShortUrl for preview
      if (form.ShortUrl) {
        window.open(form.ShortUrl, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes')
        return
      }
      
      // Generate a patient-specific form URL using the store action
      if (form.FormIDForWeb) {
        try {
          // Get patient data from conversationsV2 store
          const patientData = this.$store.state.conversationsV2.currentPatientForSms
          
          if (!patientData || !patientData.PatNum) {
            console.error('[FormModal] Patient data not available:', patientData)
            this.$toast.error('Patient information not available for preview')
            return
          }
          
          const payload = {
            siteID: this.siteID,
            formIDForWeb: form.FormIDForWeb,
            patNum: patientData.PatNum
          }
          console.log('[FormModal] Generating form URL with payload:', payload)
          
          const response = await this.$store.dispatch('forms/getFormUrl', payload)
          const formUrl = response.data
          console.log('[FormModal] Generated form URL:', formUrl)
          
          if (formUrl) {
            window.open(formUrl, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes')
          } else {
            this.$toast.warning('Could not generate preview URL for this form')
          }
        } catch (error) {
          console.error('[FormModal] Error generating form URL:', error)
          this.$toast.error('Failed to generate form preview URL')
        }
      } else {
        console.log('[FormModal] No FormIDForWeb available for form:', form)
        this.$toast.warning('Preview not available for this form')
      }
    },
    
    sendForm() {
      if (!this.selectedForm) return
      
      this.$emit('send', {
        FormIDForWeb: this.selectedForm.FormIDForWeb,
        Name: this.selectedForm.Name || this.selectedForm.FormName,
        Description: this.selectedForm.Description,
        FormType: this.selectedForm.FormType,
        url: this.getFormUrl(this.selectedForm)
      })
    },
    
    getFormUrl(form) {
      // Construct the form URL - adjust based on your form system
      const baseUrl = process.env.VUE_APP_FORM_BASE_URL || window.location.origin
      return `${baseUrl}/form/${form.FormIDForWeb}`
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      return moment(dateString).format('MMM D, YYYY')
    },
    
    handleOverlayClick() {
      this.$emit('cancel')
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  
  h3 {
    margin: 0;
    color: #333;
    font-size: 18px;
    
    .fa {
      margin-right: 8px;
      color: #4caf50;
    }
  }
  
  .close-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    color: #666;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      background: #f5f5f5;
    }
  }
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.search-section {
  margin-bottom: 20px;
  
  .search-input-container {
    position: relative;
    
    .fa-search {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: #666;
    }
    
    .search-input {
      width: 100%;
      padding: 12px 12px 12px 40px;
      border: 1px solid #ddd;
      border-radius: 8px;
      font-size: 14px;
      outline: none;
      
      &:focus {
        border-color: #4caf50;
        box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
      }
    }
  }
}

.form-list {
  .form-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    margin-bottom: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      border-color: #4caf50;
      background: #f8fff8;
    }
    
    &.selected {
      border-color: #4caf50;
      background: #e8f5e8;
    }
    
    .form-info {
      flex: 1;
      
      .form-title {
        font-weight: 600;
        color: #333;
        margin-bottom: 4px;
      }
      
      .form-description {
        color: #666;
        font-size: 13px;
        line-height: 1.4;
        margin-bottom: 8px;
      }
      
      .form-meta {
        display: flex;
        gap: 12px;
        font-size: 12px;
        align-items: center;
        
        .form-type {
          padding: 2px 8px;
          border-radius: 12px;
          font-weight: 500;
          text-transform: uppercase;
          font-size: 10px;
          
          &.type-intake {
            background: #e3f2fd;
            color: #1976d2;
          }
          
          &.type-consent {
            background: #fff3e0;
            color: #f57c00;
          }
          
          &.type-medical {
            background: #fce4ec;
            color: #c2185b;
          }
          
          &.type-insurance {
            background: #e8f5e8;
            color: #388e3c;
          }
          
          // Default style
          &:not([class*="type-"]) {
            background: #f0f0f0;
            color: #666;
          }
        }
        
        .form-updated {
          color: #999;
        }
      }
    }
    
    .form-actions {
      color: #666;
      
      .fa {
        font-size: 14px;
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        transition: all 0.2s ease;
        
        &:hover {
          color: #007bff;
          background-color: #f8f9fa;
          transform: scale(1.1);
        }
      }
    }
  }
}

.loading-state, .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #666;
  
  .fa {
    font-size: 24px;
    margin-bottom: 8px;
  }
}

.selected-preview {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
  
  h4 {
    margin: 0 0 12px 0;
    font-size: 14px;
    color: #666;
  }
  
  .preview-card {
    background: #f8fff8;
    border: 1px solid #e8f5e8;
    border-radius: 8px;
    padding: 16px;
    
    strong {
      color: #4caf50;
      display: block;
      margin-bottom: 8px;
    }
    
    p {
      margin: 0 0 12px 0;
      color: #666;
      font-size: 13px;
    }
    
    .preview-details {
      .detail-item {
        display: flex;
        margin-bottom: 6px;
        font-size: 12px;
        
        .label {
          font-weight: 600;
          color: #666;
          width: 60px;
          flex-shrink: 0;
        }
        
        .value {
          color: #333;
          word-break: break-all;
        }
      }
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid #e0e0e0;
  
  .btn {
    padding: 10px 20px;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s ease;
    
    &.btn-primary {
      background: #4caf50;
      color: white;
      
      &:hover:not(:disabled) {
        background: #43a047;
      }
      
      &:disabled {
        background: #ccc;
        cursor: not-allowed;
      }
    }
    
    &.btn-secondary {
      background: #f5f5f5;
      color: #666;
      
      &:hover {
        background: #e0e0e0;
      }
    }
  }
}

// Mobile responsiveness
@media (max-width: 768px) {
  .modal-overlay {
    padding: 10px;
  }
  
  .modal-content {
    max-height: 90vh;
  }
  
  .modal-header, .modal-body, .modal-footer {
    padding: 16px;
  }
}
</style>
